import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DangerousGoodItem, DangerousGoodService, LanguageService, TransportMode } from '@hazmatsuite-next/ui';
import { Store } from '@ngrx/store';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { loadPackagingTypes, loadUnPackagingTypes, updateTmpPackage } from '../../../../store/package-list.actions';
import { tap, map, Observable, take, debounceTime } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as uuid from "uuid";
import { InnerPackagingItemEditorComponent } from '../inner-packaging-item-editor/inner-packaging-item-editor.component';
import { CompositePackagingModel, Packages, PackagingModel, PackagingType, EasyDgdService, InnerPackagingModel, EnumPackageType } from '@hazmatsuite-next/easydgd-lib';

@UntilDestroy()
@Component({
  selector: 'easydgd-composite-packaging-item-editor',
  templateUrl: './composite-packaging-item-editor.component.html',
  styleUrls: ['./composite-packaging-item-editor.component.scss'],
})
export class CompositePackagingItemEditorComponent implements OnInit {
  @ViewChild('innerPackaging', { static: false }) innerPackaging?: InnerPackagingItemEditorComponent;
  @Input() transportMode: TransportMode = TransportMode.Unknown;
  @Output() valid = new EventEmitter<boolean>;
  @Output() packageChanged = new EventEmitter<CompositePackagingModel>();

  model: CompositePackagingModel = new CompositePackagingModel();
  formGroup: FormGroup;
  innerPackagingModalOpen = false;
  isEditModal = false;
  innerPackingIsValid = false;

  private internalIdsForLoadedDg: string[] = [];

  public packageTypes$ = this.store.select(state => {
    if (state.packageList.tempPackage.PackageType === EnumPackageType.Tank) {
      return state.packageList.packagingTypesTank;
    }
    if (state.packageList.tempPackage.PackageType === EnumPackageType.Bulk) {
      return state.packageList.packagingTypesBulk;
    }
    return state.packageList.packagingTypes;
  });

  public packagingUnCodes$ = this.store.select(state => state.packageList.packagingUnCodes);
  public packageTypesLoaded$ = this.store.select(state => state.packageList.packagingTypesLoaded).pipe(
    untilDestroyed(this)
  ).subscribe((loaded) => {
    if (!loaded) {
      this.store.dispatch(loadPackagingTypes({ packageType: EnumPackageType.CompositePacking, transportMode: this.easyDgdService.getTransportMode() }));
      this.store.dispatch(loadPackagingTypes({ packageType: EnumPackageType.Tank, transportMode: this.easyDgdService.getTransportMode() }));
      this.store.dispatch(loadPackagingTypes({ packageType: EnumPackageType.Bulk, transportMode: this.easyDgdService.getTransportMode() }));
    }
  });

  public package$ = this.store.select(state => state.packageList.tempPackage).pipe(
    map(pkg => pkg as CompositePackagingModel),
    tap((pkg: CompositePackagingModel) => {
      this.internalIdsForLoadedDg = [];
      const itemsWithoudDg = pkg.Items?.filter(item => !item.DangerousGood && item.DangerousGoodId && !this.internalIdsForLoadedDg.some(id => id === item.InternalId));
      if (itemsWithoudDg?.length) {
        itemsWithoudDg.forEach(item => {
          this.dangerousGoodService.getDangerousGood(item.DangerousGoodId as number, this.transportMode).pipe(take(1)).subscribe((dangerousGood: DangerousGoodItem) => {
            this.internalIdsForLoadedDg.push(item.InternalId);
            this.store.dispatch(updateTmpPackage({
              tempPackage: {
                ...pkg,
                Items: pkg.Items.map(i => {
                  if (i.InternalId === item.InternalId) {
                    return {
                      ...i,
                      DangerousGood: dangerousGood
                    };
                  }
                  return {
                    ...i
                  };
                })
              }
            }));
          });
        });
        return;
      }
      this.countOfPackagings?.setValue(pkg.CountOfPackagings === 0 ? 1 : pkg.CountOfPackagings, { onlySelf: true });
      this.packagingType?.setValue(pkg.PackagingType, { onlySelf: true });
      this.packagingUnCode?.setValue((pkg.PackagingUnCode) || 0, { onlySelf: true });
      this.grossWeight?.setValue(pkg.GrossWeightValue, { onlySelf: true });
      this.isTotalQuantity?.setValue((pkg.IsTotalQuantity || false), { onlySelf: true });
      this.model = pkg;
    })
  );

  public singlePackagingResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['SinglePackagingResource']));

  public compositePackagingResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['CompositePackagingResource']));

  public validationResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['ValidationResource']));

  public packingListResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['PackingListResource']));
  public layoutResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['LayoutResource']));
  public currentInnerPackage: InnerPackagingModel = new InnerPackagingModel();
  public currentInnerPackageModel?: InnerPackagingModel;

  constructor(
    builder: FormBuilder,
    private store: Store<{ packageList: { packagingTypesLoaded: boolean, packages: Packages, tempPackage: PackagingModel, packagingTypes: PackagingType[], packagingTypesTank: PackagingType[], packagingTypesBulk: PackagingType[], packagingUnCodes: PackagingType[] } }>,
    private languageService: LanguageService,
    private easyDgdService: EasyDgdService,
    private dangerousGoodService: DangerousGoodService
  ) {
    this.formGroup = builder.group({
      countOfPackagings: new FormControl(1, [Validators.required, Validators.min(1)]),
      packagingType: new FormControl('4G', (ctrl: AbstractControl) =>
        Validators.required(ctrl)
      ),
      packagingUnCode: new FormControl(0, Validators.required),
      isTotalQuantity: new FormControl(false, Validators.required),
      grossWeight: new FormControl(null, [Validators.required, Validators.min(0.001)]),
    });

    this.formGroup.valueChanges.pipe(debounceTime(100)).subscribe((value) => {
      this.model = {
        ...this.model,
        CountOfPackagings: value.countOfPackagings,
        GrossWeightValue: value.grossWeight,
        IsTotalQuantity: JSON.parse(value.isTotalQuantity),
        PackagingType: value.packagingType,
        PackagingUnCode: value.packagingUnCode
      };
      this.valid.emit(this.formGroup.valid && this.model?.Items?.length > 0);
      this.packageChanged.emit(this.model);
    });
  }

  ngOnInit(): void {
    this.packagingType?.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(val => {
      this.store.dispatch(loadUnPackagingTypes({ packagingTypeId: val, transportMode: this.easyDgdService.getTransportMode(), packageType: EnumPackageType.CompositePacking }));
    });
  }

  editPackage(innerPackage: InnerPackagingModel): void {
    this.currentInnerPackage = innerPackage;
    this.isEditModal = true;
    this.openInnerPackagingModal();
  }

  currentInnerPackageChanged(pkg: InnerPackagingModel): void {
    this.currentInnerPackageModel = pkg;
  }

  addInnerPackaging(): void {
    const pkg = new InnerPackagingModel();

    pkg.InternalId = uuid.v4();
    this.currentInnerPackage = pkg;
    this.openInnerPackagingModal();
  }

  addPackage(): void {
    this.model = {
      ...this.model,
      Items: [...this.model.Items, (this.currentInnerPackageModel as InnerPackagingModel)]
    }
    this.innerPackagingModalOpen = false;
    this.valid.emit(this.formGroup.valid && this.model?.Items?.length > 0);
    this.packageChanged.emit(this.model);
    this.innerPackaging?.formGroup.reset();
  }

  updatePackage(): void {
    this.model = {
      ...this.model,
      Items: [...this.model.Items.map(item => {
        if (item.InternalId === (this.currentInnerPackageModel as InnerPackagingModel).InternalId) {
          return this.currentInnerPackageModel as InnerPackagingModel;
        }
        return item;
      })]
    }
    this.innerPackagingModalOpen = false;
    this.valid.emit(this.formGroup.valid && this.model?.Items?.length > 0);
    this.packageChanged.emit(this.model);
    this.innerPackaging?.formGroup.reset();
  }

  openInnerPackagingModal(): void {
    this.innerPackagingModalOpen = true;
  }

  trackByCode(_: number, type: PackagingType) {
    return type.Id;
  }

  public changePackagingType(evt: any): void {
    this.packagingType?.setValue(evt.currentTarget.value, { onlySelf: true });
  }

  public changePackagingUnCode(evt: any): void {
    this.packagingUnCode?.setValue(evt.currentTarget.value, { onlySelf: true });
  }

  public markChildAsTouched(): void {
    this.innerPackaging?.markAsTouched();
  }

  public markAsTouched(): void {
    this.formGroup.markAllAsTouched();
  }

  get countOfPackagings(): AbstractControl | null {
    return this.formGroup?.get('countOfPackagings');
  }
  get packagingType(): AbstractControl | null {
    return this.formGroup?.get('packagingType');
  }
  get packagingUnCode(): AbstractControl | null {
    return this.formGroup?.get('packagingUnCode');
  }
  get grossWeight(): AbstractControl | null {
    return this.formGroup?.get('grossWeight');
  }
  get isTotalQuantity(): AbstractControl | null {
    return this.formGroup?.get('isTotalQuantity');
  }
}
